import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, MessageSquarePlus, Send } from 'lucide-react';
import { employeeRemarksAPI } from '../services/api';
import { BlockLoader } from './loading';

// Shared "remarks" (call-notes) modal — one append-only thread per employee, NOT
// per day. Used from the Mood Report table AND the Dashboard Low-mood watchlist, so
// the SAME history shows wherever the employee appears. author identity comes from
// the token server-side; `authorName` is a best-effort display name (the caller's).

interface Remark {
  id: number;
  body: string;
  authorCode: string | null;
  authorName: string;
  createdAt: string | null;
}

const fmtDateTime = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

export default function EmployeeRemarksModal({
  open,
  onClose,
  employeeCode,
  employeeName,
  projectId,
  authorName,
  onChanged,
}: {
  open: boolean;
  onClose: () => void;
  employeeCode: string;
  employeeName?: string;
  projectId?: number;
  /** The logged-in user's display name, stored as the remark author (best-effort). */
  authorName?: string;
  /** Fired after a remark is added, so callers can bump their "N notes" badge. */
  onChanged?: () => void;
}) {
  const [remarks, setRemarks] = useState<Remark[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const reqRef = useRef(0);

  useEffect(() => {
    if (!open || !employeeCode) return;
    const reqId = ++reqRef.current;
    setLoading(true);
    setError(null);
    setDraft('');
    employeeRemarksAPI
      .list({ employeeCode, projectId })
      .then((res) => {
        if (reqId !== reqRef.current) return;
        setRemarks((res.data?.data?.remarks as Remark[]) || []);
      })
      .catch(() => { if (reqId === reqRef.current) setError('Failed to load remarks'); })
      .finally(() => { if (reqId === reqRef.current) setLoading(false); });
  }, [open, employeeCode, projectId]);

  const submit = async () => {
    const body = draft.trim();
    if (!body || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await employeeRemarksAPI.add({ employeeCode, projectId, body, authorName });
      const created = res.data?.data as Remark | undefined;
      if (created) setRemarks((prev) => [created, ...prev]);
      setDraft('');
      onChanged?.();
    } catch {
      setError('Failed to add remark');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between border-b border-gray-100 p-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-base font-semibold text-gray-900">
              <MessageSquarePlus className="h-4 w-4 text-primary-600" /> Remarks
            </div>
            <div className="mt-0.5 truncate text-sm text-gray-500">
              {employeeName || employeeCode}
              {employeeName ? <span className="ml-1 font-mono text-xs text-gray-400">{employeeCode}</span> : null}
            </div>
          </div>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>

        {/* Thread */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {loading ? (
            <BlockLoader className="h-24" />
          ) : error && remarks.length === 0 ? (
            <div className="py-6 text-center text-sm text-rose-600">{error}</div>
          ) : remarks.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-200 py-8 text-center text-sm text-gray-400">
              No remarks yet. Add the first note below.
            </div>
          ) : (
            <div className="space-y-2.5">
              {remarks.map((r) => (
                <div key={r.id} className="rounded-lg border border-gray-100 bg-gray-50/70 p-3">
                  <div className="mb-1 flex items-center justify-between gap-2 text-[11px] text-gray-500">
                    <span className="font-medium text-gray-700">{r.authorName}</span>
                    <span>{fmtDateTime(r.createdAt)}</span>
                  </div>
                  <p className="whitespace-pre-wrap text-sm text-gray-800">{r.body}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add */}
        <div className="shrink-0 border-t border-gray-100 p-3">
          {error && remarks.length > 0 && <div className="mb-2 text-xs text-rose-600">{error}</div>}
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add a remark (e.g. called on 10 Jul — will sort out store WiFi tomorrow)…"
            rows={2}
            className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={submit}
              disabled={!draft.trim() || saving}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
            >
              <Send className="h-3.5 w-3.5" /> {saving ? 'Adding…' : 'Add remark'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
